import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { formatPhoneInput } from "@/lib/phone";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ITEM_OPTIONS = [
  { v: "Regular Medication", label: "Regular Medication" },
  { v: "Controlled Medication", label: "Controlled Medication" },
  { v: "Refrigerated", label: "Refrigerated" },
  { v: "Other", label: "Other" },
];

const DELIVER_TYPES = [
  { v: "same_day", label: "Same Day" },
  { v: "next_day", label: "Next Day" },
  { v: "otc", label: "OTC" },
  { v: "pickup", label: "Pick Up" },
];

export default function OrderEditDialog({ orderId, open, onClose, onUpdated }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const canSetCopay = user?.role === "super_admin" || user?.role === "pharmacy_staff";
  const [copayEnabled, setCopayEnabled] = useState(false);
  // Mirror of the Copay toggle for the "Mandatory Signature" flag —
  // pre-populated from the persisted order so re-opening the dialog
  // shows the current state.
  const [mandatorySignature, setMandatorySignature] = useState(false);
  const [order, setOrder] = useState(null);
  const [pharmacies, setPharmacies] = useState([]);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", address: "",
    pharmacy_id: "", priority: "normal", deliver_type: "same_day",
    price: "", copay: "", notes: "", order_date: "",
  });
  const [items, setItems] = useState([{ name: "Regular Medication", qty: 1, notes: "" }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !orderId) return;
    setOrder(null);
    Promise.all([api.get(`/orders/${orderId}`), api.get("/pharmacies")]).then(([o, p]) => {
      const d = o.data;
      setOrder(d);
      setPharmacies(p.data);
      setForm({
        customer_name: d.customer_name || "",
        customer_phone: d.customer_phone || "",
        address: d.address || "",
        pharmacy_id: d.pharmacy_id || "",
        priority: d.priority || "normal",
        deliver_type: d.deliver_type || "same_day",
        price: typeof d.price === "number" ? String(d.price) : "",
        copay: typeof d.copay === "number" ? String(d.copay) : "",
        notes: d.notes || "",
        // YYYY-MM-DD slice of the ISO created_at — matches <input type="date">.
        order_date: (d.created_at || "").slice(0, 10),
      });
      setCopayEnabled(typeof d.copay === "number" && d.copay > 0);
      setMandatorySignature(!!d.mandatory_signature);
      const mapped = (d.items || []).map((i) => {
        // Normalize legacy item names to one of the 3 options
        const exact = ITEM_OPTIONS.find((o) => o.v === i.name);
        return { name: exact ? exact.v : "Regular Medication", qty: i.qty || 1, notes: i.notes || "" };
      });
      setItems(mapped.length ? mapped : [{ name: "Regular Medication", qty: 1, notes: "" }]);
    });
  }, [open, orderId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setItems((a) => a.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((a) => [...a, { name: "Regular Medication", qty: 1, notes: "" }]);
  const removeItem = (i) => setItems((a) => (a.length > 1 ? a.filter((_, idx) => idx !== i) : a));

  // Auto-refresh the Price when the user changes Deliver Type or Address.
  // We call the same `/pricing/quote` endpoint the New Order form uses so
  // the result tracks the pharmacy's pricing-band table + driving miles
  // recomputed for the (possibly edited) address.
  const [autoQuoting, setAutoQuoting] = useState(false);
  // Snapshot of the order's persisted address — used to detect "real" address
  // changes so we don't re-quote (and possibly clobber a manual price) just
  // because the dialog mounted.
  const originalAddrRef = useRef("");
  useEffect(() => {
    if (order) originalAddrRef.current = (order.address || "").trim();
  }, [order]);

  // Shared quote helper. Returns true when a price was applied.
  const fetchAndApplyQuote = async (deliverType, address, pharmacyId, reasonLabel) => {
    if (!pharmacyId || !(address || "").trim()) return false;
    setAutoQuoting(true);
    try {
      const { data } = await api.get("/pricing/quote", {
        params: { address: address.trim(), pharmacy_id: pharmacyId, deliver_type: deliverType },
      });
      if (typeof data?.price === "number" && data.price > 0) {
        set("price", String(data.price.toFixed(2)));
        toast.success(`Price updated to $${data.price.toFixed(2)} (${data.band} mi, ${reasonLabel})`);
        return true;
      }
      // Pharmacy has no pricing for this band/type — clear the price so it
      // doesn't carry over from the previous deliver type.
      set("price", "");
      toast.info(`No price configured for ${reasonLabel} @ ${data?.band || "?"} mi. Set one in Manage → Pharmacies.`);
      return false;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setAutoQuoting(false);
    }
  };

  const repriceForDeliverType = async (nextType) => {
    set("deliver_type", nextType);
    await fetchAndApplyQuote(nextType, form.address, form.pharmacy_id, nextType.replace("_", " "));
  };

  // Debounced re-quote when the Address is edited. Skips the initial
  // mount + any change that lands back on the persisted address (e.g.
  // user types then backspaces).
  useEffect(() => {
    if (!order) return; // wait until the order is loaded
    const typed = (form.address || "").trim();
    if (!typed || typed === originalAddrRef.current) return;
    const t = setTimeout(() => {
      fetchAndApplyQuote(form.deliver_type, typed, form.pharmacy_id, "address change");
      // After the first auto-reprice, treat this typed address as the new
      // baseline so further small edits don't re-fire until they differ.
      originalAddrRef.current = typed;
    }, 900); // tail-debounce so we don't spam OSRM on every keystroke
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.address, form.pharmacy_id, form.deliver_type]);

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = items.filter((it) => it.name.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1 }));
    if (!cleaned.length) { toast.error("Add at least one item"); return; }
    setBusy(true);
    try {
      const payload = { ...form, items: cleaned };
      if (isSuperAdmin) {
        payload.price = form.price === "" ? null : Number(form.price);
      } else {
        delete payload.price;
      }
      if (canSetCopay) {
        if (!copayEnabled) payload.copay = null;
        else payload.copay = form.copay === "" ? null : Number(form.copay);
      } else {
        delete payload.copay;
      }
      payload.mandatory_signature = !!mandatorySignature;
      // Order Date is Super Admin only. Only include when it actually changed
      // from the persisted value so we don't spuriously rewrite created_at.
      if (isSuperAdmin && form.order_date && form.order_date !== (order?.created_at || "").slice(0, 10)) {
        payload.order_date = form.order_date;
      } else {
        delete payload.order_date;
      }
      await api.put(`/orders/${orderId}`, payload);
      if (isFailed) {
        toast.success(`Order reopened for a 2nd attempt — add it to a route from Plan Route.`);
      } else {
        toast.success("Order updated");
      }
      onUpdated?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  // Slim billing-only save for delivered orders. Only deliver_type / price /
  // copay / order_date are sent; the backend rejects anything else on a closed order.
  const submitBillingOnly = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { deliver_type: form.deliver_type };
      payload.price = form.price === "" ? null : Number(form.price);
      if (canSetCopay) {
        payload.copay = copayEnabled ? (form.copay === "" ? null : Number(form.copay)) : null;
      }
      if (form.order_date && form.order_date !== (order?.created_at || "").slice(0, 10)) {
        payload.order_date = form.order_date;
      }
      await api.put(`/orders/${orderId}`, payload);
      toast.success("Billing updated");
      onUpdated?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  // Delivered orders are locked to Super Admin's billing-only form.
  // Failed orders now get the FULL edit form so any operator (Super Admin,
  // Dispatcher, Pharmacy Staff) can correct the address / phone / etc.
  // and the backend auto-reopens the order for a 2nd attempt.
  const isDelivered = order && order.status === "delivered";
  const isFailed = order && order.status === "failed";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-2xl w-[95vw] p-0 max-h-[90vh] flex flex-col overflow-hidden"
        data-testid="order-edit-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0">
          <DialogTitle>Edit order</DialogTitle>
        </DialogHeader>
        {!order && <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>}
        {order && isDelivered && !isSuperAdmin && (
          <div className="py-4 px-5 text-sm text-muted-foreground">
            This order is already <span className="font-semibold">{order.status}</span> and cannot be edited.
          </div>
        )}
        {order && isDelivered && isSuperAdmin && (
          <form onSubmit={submitBillingOnly} className="flex-1 flex flex-col min-h-0" data-testid="order-edit-billing-only-form">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div className="rounded-lg border border-amber-300/60 bg-amber-50 text-amber-900 px-3 py-2 text-xs">
                This order is <span className="font-semibold">{order.status}</span>. Super Admin can only adjust
                <span className="font-semibold"> Deliver Type</span>, <span className="font-semibold">Price</span>
                {canSetCopay ? <>, <span className="font-semibold">Copay</span></> : null} and
                <span className="font-semibold"> Order Date</span>. Every change is written to the order history.
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Deliver type</Label>
                  <Select value={form.deliver_type} onValueChange={(v) => set("deliver_type", v)}>
                    <SelectTrigger data-testid="edit-deliver-type-billing"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DELIVER_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.price}
                    onChange={(e) => set("price", e.target.value)}
                    placeholder="0.00"
                    data-testid="edit-price-billing"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Order date</Label>
                  <Input
                    type="date"
                    value={form.order_date}
                    onChange={(e) => set("order_date", e.target.value)}
                    data-testid="edit-order-date-billing"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Fix the date this order was originally recorded (used by reports and billing periods). Time-of-day is preserved.
                  </p>
                </div>
                {canSetCopay && (
                  <div data-testid="edit-copay-section-billing" className="md:col-span-2">
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={copayEnabled}
                        onCheckedChange={(v) => {
                          const next = !!v;
                          setCopayEnabled(next);
                          if (!next) set("copay", "");
                        }}
                        data-testid="edit-copay-toggle-billing"
                      />
                      <span className="text-sm">Charge a copay</span>
                    </Label>
                    {copayEnabled && (
                      <Input
                        className="mt-1.5"
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.copay}
                        onChange={(e) => set("copay", e.target.value)}
                        placeholder="0.00"
                        data-testid="edit-copay-billing"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-card shrink-0">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-full" data-testid="edit-submit-billing">{busy ? "Saving…" : "Save billing changes"}</Button>
            </div>
          </form>
        )}
        {order && !isDelivered && (
          <form onSubmit={submit} className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="order-edit-scroll">
            {isFailed && (
              <div className="rounded-lg border border-amber-300/60 bg-amber-50 text-amber-900 px-3 py-2 text-xs" data-testid="edit-failed-reopen-notice">
                This order is <span className="font-semibold">Failed</span>. Save your fixes (e.g. address / phone)
                and it will be <span className="font-semibold">reopened for a 2nd delivery attempt</span> —
                you can slot it into a new route from Plan Route.
              </div>
            )}            <div>
              <Label>Pharmacy</Label>
              <Select value={form.pharmacy_id} onValueChange={(v) => set("pharmacy_id", v)}>
                <SelectTrigger data-testid="edit-pharmacy"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Patient name</Label>
                <Input required value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="edit-customer-name" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input required value={form.customer_phone} onChange={(e) => set("customer_phone", formatPhoneInput(e.target.value))} placeholder="305-555-1234" data-testid="edit-customer-phone" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input required value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="edit-address" />
              <p className="text-xs text-muted-foreground mt-1">
                Changing this re-geocodes the location {autoQuoting ? "and recomputes the price…" : "and recomputes the price."}
              </p>
              {order?.route_id && (form.address || "").trim() !== (order.address || "").trim() && order.status !== "delivered" && order.status !== "failed" && (
                <div
                  className="mt-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-start gap-2"
                  data-testid="order-route-warning"
                >
                  <span className="text-base leading-none">⚠️</span>
                  <span>
                    Saving this change will <span className="font-semibold">recompute the active route</span>
                    {order.route_label ? ` "${order.route_label}"` : ""} (this stop's coords and the route polyline / ETA will refresh).
                  </span>
                </div>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Deliver type</Label>
                <Select value={form.deliver_type} onValueChange={(v) => repriceForDeliverType(v)}>
                  <SelectTrigger data-testid="edit-deliver-type" disabled={autoQuoting}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DELIVER_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {autoQuoting && <p className="text-[11px] text-muted-foreground mt-1">Recomputing price…</p>}
              </div>
              <div>
                <Label>Price ($)</Label>
                {isSuperAdmin ? (
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.price}
                    onChange={(e) => set("price", e.target.value)}
                    placeholder="Auto from pharmacy pricing"
                    data-testid="edit-price"
                  />
                ) : (
                  <div className="h-10 px-3 rounded-md border border-input bg-secondary/40 text-sm flex items-center text-muted-foreground" data-testid="edit-price-readonly">
                    {form.price === "" || form.price == null ? "Auto" : `$${Number(form.price).toFixed(2)}`}
                    <span className="ml-auto text-[10px] uppercase tracking-wide">Super Admin only</span>
                  </div>
                )}
              </div>
              {canSetCopay && (
                <div data-testid="edit-copay-section">
                  <Label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={copayEnabled}
                      onCheckedChange={(v) => {
                        const next = !!v;
                        setCopayEnabled(next);
                        if (!next) set("copay", "");
                      }}
                      data-testid="edit-copay-toggle"
                    />
                    <span className="text-sm">Charge a copay</span>
                  </Label>
                  {copayEnabled && (
                    <Input
                      className="mt-1.5"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.copay}
                      onChange={(e) => set("copay", e.target.value)}
                      placeholder="0.00"
                      data-testid="edit-copay"
                      autoFocus
                    />
                  )}
                </div>
              )}
              <div data-testid="edit-signature-section">
                <Label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={mandatorySignature}
                    onCheckedChange={(v) => setMandatorySignature(!!v)}
                    data-testid="edit-signature-toggle"
                  />
                  <span className="text-sm">Mandatory Signature</span>
                </Label>
                {mandatorySignature && (
                  <p className="text-[11px] text-destructive mt-1">
                    Driver will be alerted <strong>not to leave at the front door</strong>.
                  </p>
                )}
              </div>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="edit-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="cold_chain">Cold Chain</SelectItem>
                  <SelectItem value="clinic">Clinic</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isSuperAdmin && (
              <div>
                <Label>Order date</Label>
                <Input
                  type="date"
                  value={form.order_date}
                  onChange={(e) => set("order_date", e.target.value)}
                  data-testid="edit-order-date"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Super Admin only — used by reports and billing periods. Time-of-day is preserved.
                </p>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
              </div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <div className="col-span-7">
                      <Select value={it.name} onValueChange={(v) => setItem(i, "name", v)}>
                        <SelectTrigger data-testid={`edit-item-name-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ITEM_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input className="col-span-2" type="number" min={1} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`edit-item-qty-${i}`} />
                    <Input className="col-span-2" placeholder="notes" value={it.notes} onChange={(e) => setItem(i, "notes", e.target.value)} />
                    <Button type="button" variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(i)}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Delivery notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="edit-notes" />
            </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-card shrink-0">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-full" data-testid="edit-submit">{busy ? "Saving…" : "Save changes"}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
